import React, { useState, useEffect } from 'react'
import { X, Mail, Check, User, Crown, Send, AlertCircle } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import emailjs from '@emailjs/browser'

interface User {
  id: string
  name: string
  email?: string
  role: string
}

interface TestEmailModalProps {
  isOpen: boolean
  onClose: () => void
  emailConfig: {
    serviceId: string
    publicKey: string
    templateId: string
  }
  onShowMessage: (type: 'success' | 'error' | 'info', message: string) => void
}

export const TestEmailModal: React.FC<TestEmailModalProps> = ({
  isOpen,
  onClose,
  emailConfig,
  onShowMessage
}) => {
  const [admins, setAdmins] = useState<User[]>([])
  const [selectedAdmins, setSelectedAdmins] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSending, setIsSending] = useState(false)

  useEffect(() => {
    if (isOpen) {
      loadAdmins()
    }
  }, [isOpen])

  const loadAdmins = async () => {
    try {
      setIsLoading(true)
      const { data: adminUsers, error } = await supabase
        .from('users')
        .select('id, name, email, role')
        .eq('role', 'admin')
        .order('name')

      if (error) {
        console.error('Error loading admins:', error)
        onShowMessage('error', 'Fout bij het laden van admins')
        return
      }

      if (adminUsers) {
        setAdmins(adminUsers)
        // Pre-select all admins by default
        setSelectedAdmins(adminUsers.map(admin => admin.id))
      }
    } catch (error) {
      console.error('Error loading admins:', error)
      onShowMessage('error', 'Fout bij het laden van admins')
    } finally {
      setIsLoading(false)
    }
  }

  const handleAdminToggle = (adminId: string) => {
    setSelectedAdmins(prev => 
      prev.includes(adminId)
        ? prev.filter(id => id !== adminId)
        : [...prev, adminId]
    )
  }

  const sendTestEmail = async () => {
    try {
      setIsSending(true)
      onShowMessage('info', 'Test emails worden verstuurd...')

      if (selectedAdmins.length === 0) {
        onShowMessage('error', 'Selecteer minimaal één admin om test email te versturen')
        return
      }

      // Validate EmailJS config
      if (!emailConfig.serviceId || !emailConfig.publicKey || !emailConfig.templateId) {
        onShowMessage('error', 'EmailJS configuratie is niet compleet. Controleer de instellingen.')
        return
      }

      // Initialize EmailJS
      emailjs.init(emailConfig.publicKey)

      const selectedAdminData = admins.filter(admin => selectedAdmins.includes(admin.id))
      let successCount = 0
      let errorCount = 0

      // Send email to each selected admin
      for (const admin of selectedAdminData) {
        if (!admin.email) {
          console.warn(`Admin ${admin.name} heeft geen email adres`)
          errorCount++
          continue
        }

        try {
          const templateParams = {
            to_name: admin.name,
            to_email: admin.email,
            from_name: 'KVH Dashboard',
            subject: 'Test Email - KVH Dashboard',
            message: `Hallo ${admin.name},

Dit is een test email van het KVH Dashboard systeem.

Als je dit bericht ontvangt, werkt de email configuratie correct!

Test details:
- Verzonden op: ${new Date().toLocaleString('nl-NL')}
- Dashboard URL: ${window.location.origin}
- Service: ${emailConfig.serviceId}

Met vriendelijke groet,
KVH Dashboard Team`,
            report_date: new Date().toLocaleDateString('nl-NL'),
            report_type: 'Test Email'
          }

          await emailjs.send(
            emailConfig.serviceId,
            emailConfig.templateId,
            templateParams
          )

          console.log(`✅ Test email sent to ${admin.name} (${admin.email})`)
          successCount++
        } catch (emailError) {
          console.error(`❌ Failed to send email to ${admin.name}:`, emailError)
          errorCount++
        }
      }

      // Show result message
      if (successCount > 0 && errorCount === 0) {
        onShowMessage('success', `Test email succesvol verstuurd naar ${successCount} admin(s)!`)
      } else if (successCount > 0 && errorCount > 0) {
        onShowMessage('info', `${successCount} emails verstuurd, ${errorCount} mislukt. Controleer de console voor details.`)
      } else {
        onShowMessage('error', `Alle emails mislukt (${errorCount}). Controleer EmailJS configuratie en email adressen.`)
      }

      // Close modal after successful send
      if (successCount > 0) {
        setTimeout(() => {
          onClose()
        }, 2000)
      }

    } catch (error) {
      console.error('Error sending test emails:', error)
      onShowMessage('error', 'Fout bij het versturen van test emails')
    } finally {
      setIsSending(false)
    }
  }

  const getAvatarInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .substring(0, 2)
      .toUpperCase()
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="bg-blue-600 text-white p-4 rounded-t-lg flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Mail size={24} />
            <h2 className="text-xl font-bold">Test Email Versturen</h2>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-blue-200 transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-2">
              Selecteer Admin(s) voor Test Email
            </h3>
            <p className="text-sm text-gray-600">
              Kies naar welke administrators je een test email wilt versturen
            </p>
          </div>

          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p className="text-gray-500">Admins laden...</p>
            </div>
          ) : (
            <>
              {/* Admin Selection */}
              <div className="space-y-3 mb-6">
                {admins.map((admin) => (
                  <div
                    key={admin.id}
                    className={`border-2 rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
                      selectedAdmins.includes(admin.id)
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => handleAdminToggle(admin.id)}
                  >
                    <div className="flex items-center gap-3">
                      {/* Selection Indicator */}
                      <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                        selectedAdmins.includes(admin.id)
                          ? 'bg-blue-500 border-blue-500'
                          : 'border-gray-300'
                      }`}>
                        {selectedAdmins.includes(admin.id) && (
                          <Check size={16} className="text-white" />
                        )}
                      </div>

                      {/* Avatar */}
                      <div className="w-12 h-12 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold">
                        {getAvatarInitials(admin.name)}
                      </div>

                      {/* Admin Info */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-gray-800">{admin.name}</h4>
                          <Crown size={16} className="text-yellow-500" />
                        </div>
                        <p className="text-sm text-gray-600">
                          {admin.email || 'Geen email adres'}
                        </p>
                        {!admin.email && (
                          <div className="flex items-center gap-1 mt-1">
                            <AlertCircle size={14} className="text-red-500" />
                            <span className="text-xs text-red-500">Email vereist voor test</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Summary */}
              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-gray-800 mb-2">Test Email Details</h4>
                <div className="text-sm text-gray-600 space-y-1">
                  <p><strong>Ontvangers:</strong> {selectedAdmins.length} admin(s)</p>
                  <p><strong>Template:</strong> {emailConfig.templateId}</p>
                  <p><strong>Service:</strong> {emailConfig.serviceId}</p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
          >
            Annuleren
          </button>
          <button
            onClick={sendTestEmail}
            disabled={isSending || selectedAdmins.length === 0 || isLoading}
            className="px-6 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {isSending ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Versturen...
              </>
            ) : (
              <>
                <Send size={16} />
                Test Email Versturen ({selectedAdmins.length})
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}